import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Radio,
  FormLabel,
  Divider,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Paper,
  Button,
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Navigation as NavigationIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { NavigationStyle, ThemeVariant, LookAndFeelConfig } from '../../types/admin';
import { NavigationVariant } from '../../components/layout/NavigationVariants';

interface LookAndFeelTabProps {
  lookAndFeel: LookAndFeelConfig;
  onChange: (field: keyof LookAndFeelConfig, value: NavigationStyle | ThemeVariant | 'light' | 'dark') => void;
}

export const LookAndFeelTab: React.FC<LookAndFeelTabProps> = ({ lookAndFeel, onChange }) => {

  const navigationOptions = [
    { value: 'current', label: 'Current Style', description: 'Bottom border with subtle hover' },
    { value: 'pill', label: 'Pill Style', description: 'Rounded buttons with filled active state' },
    { value: 'underline', label: 'Minimal Underline', description: 'Clean with animated underline' },
    { value: 'elevated', label: 'Modern Elevated', description: 'Cards with hover elevation' },
    { value: 'icon-badge', label: 'Icon Badge', description: 'Vertical layout with icon badges' },
    { value: 'segmented', label: 'Segmented Control', description: 'iOS-style connected buttons' },
  ];

  const themeOptions = [
    { value: 'heritage', label: 'Heritage', color: '#8B4513', description: 'Warm brown tones' },
    { value: 'blue', label: 'Ocean Blue', color: '#1565C0', description: 'Professional blue' },
    { value: 'green', label: 'Forest Green', color: '#2E7D32', description: 'Natural green' },
    { value: 'orange', label: 'Autumn Orange', color: '#E65100', description: 'Vibrant orange' },
    { value: 'purple', label: 'Royal Purple', color: '#6A1B9A', description: 'Elegant purple' },
    { value: 'christmas', label: 'Christmas', color: '#C62828', description: 'Festive red & green' },
  ];

  return (
    <Grid container spacing={4}>
      {/* Navigation Style Section */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <NavigationIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Navigation Style
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select the navigation button style for the main menu
        </Typography>

        <Grid container spacing={2}>
          {navigationOptions.map((option) => (
            <Grid item xs={12} md={6} key={option.value}>
              <Paper
                sx={{
                  p: 2,
                  border: '2px solid',
                  borderColor: lookAndFeel.navigationStyle === option.value ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.light',
                    boxShadow: 2,
                  },
                }}
                onClick={() => onChange('navigationStyle', option.value as NavigationStyle)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                  <Radio
                    checked={lookAndFeel.navigationStyle === option.value}
                    value={option.value}
                    sx={{ mt: -1 }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'background.default',
                    borderRadius: 1,
                    p: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                  }}
                >
                  <NavigationVariant
                    variant={option.value as NavigationStyle}
                    demoMode={true}
                    demoActivePath="/people"
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/* Default Theme Section */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PaletteIcon sx={{ mr: 1.5, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Default Theme
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set the default color theme and mode for users who haven't customized their preferences
        </Typography>

        {/* Theme Variant Selection */}
        <Box sx={{ mb: 4 }}>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
            Color Theme
          </FormLabel>
          <Grid container spacing={2}>
            {themeOptions.map((theme) => (
              <Grid item xs={12} sm={6} md={4} key={theme.value}>
                <Paper
                  sx={{
                    p: 2,
                    border: '2px solid',
                    borderColor: lookAndFeel.defaultThemeVariant === theme.value ? 'primary.main' : 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.light',
                      transform: 'translateY(-2px)',
                      boxShadow: 2,
                    },
                  }}
                  onClick={() => onChange('defaultThemeVariant', theme.value as ThemeVariant)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1,
                        backgroundColor: theme.color,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {theme.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {theme.description}
                      </Typography>
                    </Box>
                    {lookAndFeel.defaultThemeVariant === theme.value && (
                      <Chip label="Selected" size="small" color="primary" />
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Theme Mode Selection */}
        <Box>
          <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
            Theme Mode
          </FormLabel>
          <ToggleButtonGroup
            value={lookAndFeel.defaultThemeMode}
            exclusive
            onChange={(_, value) => value && onChange('defaultThemeMode', value)}
            aria-label="theme mode"
            fullWidth
            sx={{ maxWidth: 400 }}
          >
            <ToggleButton value="light" aria-label="light mode">
              <LightModeIcon sx={{ mr: 1 }} />
              Light Mode
            </ToggleButton>
            <ToggleButton value="dark" aria-label="dark mode">
              <DarkModeIcon sx={{ mr: 1 }} />
              Dark Mode
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Divider />
      </Grid>

      {/* Theme Creation Section (Placeholder) */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <AddIcon sx={{ mr: 1.5, color: 'text.secondary' }} />
          <Typography variant="h6" fontWeight={600} color="text.secondary">
            Custom Themes
          </Typography>
          <Chip label="Coming Soon" size="small" sx={{ ml: 2 }} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Create and manage custom color themes for your organization
        </Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          disabled
          sx={{ textTransform: 'none' }}
        >
          Create New Theme
        </Button>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
          Theme creation tools will be available in a future update
        </Typography>
      </Grid>
    </Grid>
  );
};
